import { z } from "zod";
import {
  IssueEffortSchema,
  IssueLifecycleStateSchema,
  IssueSeveritySchema,
  IssueStatusSchema,
} from "../../enums/core";
import { OriginFileKindSchema } from "../../enums/scrape";
import { ContentBlockSchema } from "../../report/blocks";
import { MetricKeySchema } from "../../report/catalog";
import { ContentFitnessSubcriterionKeySchema } from "../../report/content-fitness/subcriteria";
import { IssueSemanticProfileSchema } from "./semantics";

// One affected page for an issue, sourced from the page_issues M2M. The
// per-page severity override falls back to the issue-level severity when null.
export const IssuePageLinkSchema = z.object({
  pageId: z.uuid(),
  path: z.string(),
  locator: z.string().nullable(),
  evidenceSnippet: z.string().nullable(),
  severityOverride: IssueSeveritySchema.nullable(),
});
export type IssuePageLink = z.infer<typeof IssuePageLinkSchema>;

// JSON-facing mirror of the `issues` table row plus its page links. Dates
// arrive as ISO strings over the wire; z.coerce.date() parses them back so the
// client gets Date.
export const IssueSchema = z.object({
  id: z.uuid(),
  websiteId: z.uuid(),
  title: z.string(),
  metricKey: MetricKeySchema,
  primarySubcriterionKey: ContentFitnessSubcriterionKeySchema.nullable(),
  // Set when the issue tracks an origin resource (robots|llms); the honest
  // scope label — the issue spans dimensions, not just metricKey's.
  originResourceKind: OriginFileKindSchema.nullable(),
  semanticProfile: IssueSemanticProfileSchema,
  // The canonical problem this issue tracks. NULL means no scan tracks it: it
  // was created by hand, so a re-scan never re-checks or resolves it. This is
  // the stored fact — consumers must not re-derive it from the fingerprint.
  problemKey: z.string().nullable(),
  fingerprint: z.string(),
  pages: z.array(IssuePageLinkSchema),
  severity: IssueSeveritySchema,
  impact: z.number().int(),
  effort: IssueEffortSchema,
  status: IssueStatusSchema,
  // Derived on read from the per-scan record; a separate axis from `status`.
  lifecycleState: IssueLifecycleStateSchema,
  ownerName: z.string().nullable(),
  ownerInitials: z.string().nullable(),
  firstSeenJobId: z.uuid(),
  lastSeenJobId: z.uuid(),
  blocks: z.array(ContentBlockSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Issue = z.infer<typeof IssueSchema>;
export type IssueStatusValue = z.infer<typeof IssueStatusSchema>;
